import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as CollectionInstanceActions from "../../../actions/collectionInstance";
import * as ItemComponentActions from "../../../actions/itemComponents";
import NotificationAlert from "../../../components/common/Alerts/NotificationAlert/NotificationAlert";
import { CheckboxFormInput, CustomFormInput, PrimitiveFormInput, SelectFormInput } from "../../../components/form";
import { FileUploadFormInput } from "../../../components/form/inputs/FileUploadFormInput";
import { SOURCE_DOCUMENT_UPLOAD_VIEW } from "../../../components/form/inputs/FileUploadFormInput/FileUploadViews";
import TextFormInput from "../../../components/form/inputs/TextFormInput/TextFormInput";
import ValidationMessage from "../../../components/form/validation/ValidationMessage";
import SubPageLayout from "../../../components/layouts/SubPageLayout";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { GridRemove } from "../../../enumerations/GridRemove";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock, renderRegistrationFormSaveError } from "../../../helpers/alert";
import { getInstructionsForItemComponentPrimitive, getLabelForItemComponentPrimitive } from "../../../helpers/collectionHelpers";
import {
    CollectionInstanceDataState,
    collectionInstanceIsValid,
    filterCollectionItemComponentsByDocuments,
    flattenItemComponentWithDatas,
    validateCollectionInstance
} from "../../../helpers/collectionInstanceHelper";
import { SOURCE_DOCUMENT_FILE_CONFIGURATION } from "../../../helpers/file";
import { getFormPropertyLabel, RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { findLookup, findLookupValue, generateOptionsFromLookup } from "../../../helpers/lookupHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms";
import { initialSourceDocumentState } from "../../../reducers/reactReduxForms/sourceDocument";
import { requiredSourceDocument, requiredText } from "../../../validators";

import "./CollectionInstanceComponent.scss";

interface CollectionInstanceComponentProps {
    readonly?: boolean;
    form: Dtos.CollectionInstance & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    collectionInstanceId: number;
    collection: Dtos.Collection;
    collectionInstance: Dtos.CollectionInstance;
    lookups: Dtos.Lookup[];
    loadingCollectionInstance: boolean;
    loadCollectionInstanceSuccess: boolean;
    loadCollectionInstanceFailure: boolean;
    loadCollectionInstanceErrorCode: string;
    savingCollectionInstance: boolean;
    saveCollectionInstanceSuccess: boolean;
    saveCollectionInstanceFailure: boolean;
    validationFailures: Dtos.ResponseError[];
    itemComponents: Dtos.ItemComponent[];
    loadingItemComponents: boolean;
    loadItemComponentsSuccess: boolean;
    loadItemComponentsFailure: boolean;
    loadItemComponentsErrorCode: string;
    documents: Dtos.CollectionItemComponent[];
    onSaved?: Function;
    onClose?: Function;
    clearCollectionInstance: CollectionInstanceActions.ICollectionInstanceClearActionCreator;
    loadCollectionInstance: CollectionInstanceActions.ICollectionInstanceLoadByIdActionCreator;
    saveCollectionInstance: CollectionInstanceActions.ICollectionInstanceSaveActionCreator;
    expandSection: CollectionInstanceActions.ICollectionInstanceExpandSectionActionCreator;
    clearItemComponents: ItemComponentActions.IItemComponentClearActionCreator;
    loadItemComponents: ItemComponentActions.IItemComponentLoadActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
}

const reduxFormName: string = "reduxForms.collectionInstance";

const tableHeaderClass = "btn btn-primary btn-block rounded-0 p-3 d-flex";
const tableHeaderDangerClass = "btn btn-danger btn-block rounded-0 p-3 d-flex";
const tableHeaderNaClass = "btn btn-secondary btn-block rounded-0 p-3 d-flex";
const tableStyleClass: React.CSSProperties = {
    textAlign: "left",
    fontWeight: "bold",
    color: "white",
    cursor: "pointer",
};

const CollectionInstanceComponent: FC<CollectionInstanceComponentProps> = ({
    readonly,
    form,
    formState,
    formProperties,
    collectionInstanceId,
    collection,
    collectionInstance,
    lookups,
    loadingCollectionInstance,
    //loadCollectionInstanceSuccess,
    loadCollectionInstanceFailure,
    loadCollectionInstanceErrorCode,
    savingCollectionInstance,
    saveCollectionInstanceSuccess,
    saveCollectionInstanceFailure,
    validationFailures,
    itemComponents,
    loadingItemComponents,
    //loadItemComponentsSuccess,
    loadItemComponentsFailure,
    loadItemComponentsErrorCode,
    documents,
    onSaved,
    onClose,
    clearCollectionInstance,
    loadCollectionInstance,
    saveCollectionInstance,
    expandSection,
    clearItemComponents,
    loadItemComponents,
    loadForm,
    changeForm,
    resetForm
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);
    const [isValid, setIsValid] = useState<boolean>(true);
    const [collectionInstanceDataStates, setCollectionInstanceDataStates] = useState<CollectionInstanceDataState[]>([]);

    const loadReduxForm = () => {
        let extraData: Dtos.CollectionInstance = {} as Dtos.CollectionInstance;

        loadForm(reduxFormName, Object.assign({ ...collectionInstance }, extraData));
    };

    // Load Collection Instance
    useEffect(() => {
        if (collectionInstanceId && !collectionInstance && !loadingCollectionInstance) {
            loadCollectionInstance(collectionInstanceId);
        }
        else if (collectionInstance && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        collectionInstanceId,
        collectionInstance,
        loadingCollectionInstance,
        loadCollectionInstance,
        initalised,
        loadReduxForm,
        setInitalised
    ]);

    // Load Item Components
    useEffect(() => {
        if (!itemComponents && !loadingItemComponents) {
            loadItemComponents(false, -1);
        }
    }, [
        itemComponents,
        loadingItemComponents,
        loadItemComponents
    ]);

    // Update collectionInstanceDataStates & isValid states
    useEffect(() => {
        if (form) {
            let collectionInstanceDataStatesTemp = validateCollectionInstance(
                flattenItemComponentWithDatas(form), 
                (readonly || savingCollectionInstance)
            );

            setIsValid(collectionInstanceIsValid(collectionInstanceDataStatesTemp));
            setCollectionInstanceDataStates(collectionInstanceDataStatesTemp)
        }
    }, [form]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadCollectionInstanceFailure) {
            navigate(`/error/${loadCollectionInstanceErrorCode}`);
        }
        if (loadItemComponentsFailure) {
            navigate(`/error/${loadItemComponentsErrorCode}`);
        }
    }, [
        loadCollectionInstanceFailure,
        loadCollectionInstanceErrorCode,
        loadItemComponentsFailure,
        loadItemComponentsErrorCode,
        navigate,
    ]);

    // Reload collection instance after saving
    useEffect(() => {
        if (!savingCollectionInstance && saveCollectionInstanceSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Collection instance saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (onSaved) {
                onSaved();
            }

            if (saveAndReturn) {
                onClose();
            } else {
                clearCollectionInstance();
                setInitalised(false);
            }
        }
    }, [
        savingCollectionInstance,
        saveCollectionInstanceSuccess,
        onSaved,
        navigate,
        onClose,
        saveAndReturn,
        clearCollectionInstance,
        setInitalised
    ]);

    // Failed saving collection instance
    useEffect(() => {
        if (!savingCollectionInstance && saveCollectionInstanceFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveError()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingCollectionInstance, saveCollectionInstanceFailure]);

    // Cleanup effect to clear the collection instance & item components on component unmount
    useEffect(() => {
        return () => {
            clearCollectionInstance();
            clearItemComponents();
            resetForm(reduxFormName);
        };
    }, [clearCollectionInstance, clearItemComponents, resetForm, reduxFormName]);

    const handleOnSubmit = (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.CollectionInstance = {} as Dtos.CollectionInstance;
            saveCollectionInstance(Object.assign({ ...data }, changes));
        }

        if (data.saveAndReturn) {
            setSaveAndReturn(true);
        }
    }

    const handleOnSubmitFailed = (_data: Dtos.CollectionInstance) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    }

    const onCancel = () => {
        if (onClose) {
            onClose();
        }
    }

    const renderItemComponentWithData = useCallback((
        itemComponentWithData: Dtos.ItemComponentWithData,
        reduxFormNameParent: string,
        locked: boolean
    ) => {
        if (
            itemComponentWithData.collectionInstanceData &&
            itemComponentWithData.itemComponent &&
            itemComponentWithData.itemComponentPrimitive
        ) {
            const name = `itemComponentInputPrimitives_${itemComponentWithData.collectionInstanceData.id}`;

            let collectionInstanceDataState =
                collectionInstanceDataStates.find((f) =>
                        f.id == itemComponentWithData.collectionInstanceData.id
                );
            if (collectionInstanceDataState == undefined) {
                collectionInstanceDataState = {
                    id: itemComponentWithData.collectionInstanceData.id,
                    isAnswered: false,
                    isDisabled: readonly || savingCollectionInstance || locked || itemComponentWithData.formLocked,
                    isRequired: false,
                    isValid: true,
                    validations: [],
                };
            }

            let primitiveItem = {
                ...itemComponentWithData.itemComponentPrimitive,
            };
            if (itemComponentWithData.overrideLabel) {
                primitiveItem.label = itemComponentWithData.overrideLabel;
            }

            primitiveItem.label = 
                getLabelForItemComponentPrimitive(
                    form.collection?.collectionItemComponentOverrides,
                    itemComponentWithData.collectionInstanceData.collectionItemComponent,
                    primitiveItem
                );
            primitiveItem.instructions =
                getInstructionsForItemComponentPrimitive(
                    collection?.collectionItemComponentOverrides,
                    itemComponentWithData.collectionInstanceData.collectionItemComponent,
                    primitiveItem
                );

            return (
                <div
                    key={`itemComponent-${itemComponentWithData.collectionInstanceData.id}`}
                    style={{ display: "block" }}
                >
                    <div
                        className={
                            itemComponentWithData.isQuestion &&
                            collectionInstanceDataState.isRequired &&
                            !collectionInstanceDataState.isAnswered ? "text-danger" : ""
                        }
                        style={{ width: "100%" }}
                    >
                        <PrimitiveFormInput
                            key={name}
                            item={primitiveItem}
                            id={name}
                            name={name}
                            disabled={collectionInstanceDataState.isDisabled || itemComponentWithData.collectionInstanceData.isNa}
                            textValue={
                                itemComponentWithData.collectionInstanceData.textValue
                                    ? itemComponentWithData.collectionInstanceData.textValue
                                    : ""
                            }
                            bitValue={
                                itemComponentWithData.collectionInstanceData.bitValue != null
                                    ? itemComponentWithData.collectionInstanceData.bitValue
                                    : false
                            }
                            intValue={
                                itemComponentWithData.collectionInstanceData.intValue != null
                                    ? itemComponentWithData.collectionInstanceData.intValue
                                    : null
                            }
                            numberValue={
                                itemComponentWithData.collectionInstanceData.numberValue != null
                                    ? itemComponentWithData.collectionInstanceData.numberValue
                                    : null
                            }
                            dateValue={
                                itemComponentWithData.collectionInstanceData.dateValue != null
                                    ? itemComponentWithData.collectionInstanceData.dateValue
                                    : null
                            }
                            onChange={(field, value) => {
                                changeForm(`${reduxFormNameParent}.${field}`, value);
                                changeForm(`${reduxFormNameParent}.isModified`, true);
                            }}
                            validationFailures={collectionInstanceDataState.validations}
                            isRequired={collectionInstanceDataState.isRequired || itemComponentWithData.isRequired}
                            instructions={itemComponentWithData.instructions}
                            isNaAllowed={itemComponentWithData.isNaAllowed}
                            isNa={itemComponentWithData.collectionInstanceData.isNa}
                        />
                        {(itemComponentWithData.isLastPrimitive &&
                            itemComponentWithData.itemComponent.sourceDocuments &&
                            itemComponentWithData.itemComponent.sourceDocuments.length) > 0 && (
                            <div className="card" style={{ border: "none !important" }}>
                                <div className="card-body p-3">
                                    <h6 className="card-subtitle">
                                        Downloads and Documents
                                    </h6>
                                    {itemComponentWithData.itemComponent.sourceDocuments.map((sourceDocument, sourceDocumentIndex) => (
                                        <div 
                                            key={`itemComponentWithData.ItemComponentId-${itemComponentWithData.itemComponent?.id}-sourceDocument-${sourceDocument?.id}`}
                                            className="d-flex"
                                        >
                                            <div className="flex-grow-1 py-2">
                                                {sourceDocument.name}
                                            </div>
                                            <div className="p-2 text-muted">
                                                <em>
                                                    {findLookupValue(
                                                        "ItemComponentSourceDocument",
                                                        "FileType",
                                                        sourceDocument.fileType,
                                                        lookups
                                                    )}
                                                </em>
                                            </div>
                                            <div>
                                                {SOURCE_DOCUMENT_FILE_CONFIGURATION(
                                                    form,
                                                    changeForm,
                                                    reduxFormName,
                                                    lookups,
                                                    sourceDocument,
                                                    sourceDocumentIndex
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (itemComponentWithData.itemComponent && itemComponentWithData.itemComponent.customInputControlName != "") {
            const customName = `itemComponent_${itemComponentWithData.itemComponent.id}_${itemComponentWithData.itemComponent.customInputControlName}`;
            return (
                <CustomFormInput
                    key={`CustomFormInput-${itemComponentWithData.itemComponent.id}`}
                    item={itemComponentWithData.itemComponent}
                    id={customName}
                    name={customName}
                />
            );
        }
    }, [
        collectionInstanceDataStates,
        readonly,
        savingCollectionInstance,
        form,
        collection,
        changeForm,
        findLookupValue
    ]);

    const renderContent = useMemo(() => {
        const formDisabled: boolean =
            readonly ||
            savingCollectionInstance ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);
        let formComponents: { [index: string]: RegistrationFormComponent } = {};
        let formVersionComponents: {
            [index: string]: RegistrationFormComponent;
        } = {};

        if (collectionInstance && collectionInstance.version) {
            formVersionComponents["version"] = {
                type: RegistrationComponent.RegistrationControl,
                inputType: TextFormInput,
                inputDisabled: formDisabled || !collectionInstance.canEditVersion,
                groupClassName: "row mb-3",
                labelClassName: "col-form-label col-sm-5",
                fieldClassName: "col-sm-7 generic-form-field",
            };
        }

        let sectionId: number = 0;
        let columnFormat: string = form.columnFormat;
        if (collectionInstance && collectionInstance.hasSections) {
            collectionInstance.sections.forEach((s) => {
                if (s.expanded) {
                    sectionId = s.collectionSection.id;
                    columnFormat = s.columnFormat;
                }
            });
        }
        let section = form.sections.find((s) => s.collectionSectionId == sectionId);

        if (documents) {
            documents
                .filter((f) => f.collectionSectionId == sectionId)
                .forEach((itemComponentWithDocuments) => {
                    itemComponentWithDocuments.itemComponent.itemComponentPrimitives
                        .filter((f) =>
                            f.active &&
                            f.itemPrimitive == Dtos.ItemPrimitive.Documents
                        )
                        .forEach((item) => {
                            // TO DO ~ find the data record - move this to the property mapping for performance reasons
                            const collectionInstanceData: Dtos.CollectionInstanceData =
                                form.collectionInstanceDatas.find((i) =>
                                    i.itemComponentPrimitiveId == item.id &&
                                    i.itemComponentId == item.itemComponentId
                                );

                            if (collectionInstanceData != null) {
                                formComponents[`sourceDocuments${collectionInstanceData.id}`] = {
                                    type: RegistrationComponent.RegistrationSourceDocuments,
                                    label: item.label,
                                    disabled: formDisabled || (section !== undefined && section.locked),
                                    components: {
                                        sourceDocuments: {
                                            type: RegistrationComponent.RegistrationGrid,
                                            formName: "SourceDocument",
                                            disabled: formDisabled,
                                            label: "Add Source Document",
                                            className: "source-documents-source-documents",
                                            minRows: 0,
                                            columnStyles: [
                                                {
                                                    width: undefined,
                                                    minWidth: "200px",
                                                },
                                                {
                                                    width: "23%",
                                                    minWidth: "150px",
                                                },
                                                {
                                                    width: "20%",
                                                    minWidth: "150px",
                                                },
                                                {
                                                    width: "125px",
                                                },
                                            ],
                                            removeConfig: GridRemove.All,
                                            addRow: (gridComponent, _index, _array) => {
                                                const state: Dtos.SourceDocument =
                                                    Object.assign({
                                                        ...gridComponent.initialState,
                                                    });
                                                return state;
                                            },
                                            components: {
                                                name: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextFormInput,
                                                    inputDisabled: formDisabled,
                                                    inputLabel: getFormPropertyLabel("CollectionInstance", "FileName", formProperties),
                                                    inputProps: {
                                                        feedbackIcon: FontAwesomeIcons.Solid.BAN,
                                                    },
                                                    validators: {
                                                        required: requiredText,
                                                    },
                                                    errors: {
                                                        required: "File name is required.",
                                                    },
                                                    errorComponent: ValidationMessage,
                                                },
                                                fileType: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: SelectFormInput,
                                                    inputLabel: getFormPropertyLabel("CollectionInstance", "FileType", formProperties),
                                                    inputProps: {
                                                        options:
                                                            generateOptionsFromLookup(
                                                                findLookup("CollectionSourceDocument", "FileType", lookups)
                                                            ),
                                                        feedbackIcon: FontAwesomeIcons.Solid.BAN,
                                                    },
                                                    validators: {
                                                        required: (value) => value != undefined,
                                                    },
                                                    errors: {
                                                        required: "File type is required.",
                                                    },
                                                    errorComponent: ValidationMessage,
                                                },
                                                fileInformation: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: FileUploadFormInput,
                                                    inputDisabled: formDisabled,
                                                    inputLabel: getFormPropertyLabel("CollectionInstance", "Upload", formProperties),
                                                    changeAction: (model, value, form, parentModel) => {
                                                        changeForm(model, value);
                                                        const inputValue: Dtos.TemporaryFileInformation = value;
                                                        const sourceDocument: Dtos.SourceDocument = form;

                                                        if (
                                                            sourceDocument.fileInformation &&
                                                            inputValue &&
                                                            sourceDocument.fileInformation.state != inputValue.state &&
                                                            inputValue.state == Dtos.FileState.Success &&
                                                            !sourceDocument.name
                                                        ) {
                                                            changeForm(
                                                                `${reduxFormName}${parentModel}.name`,
                                                                inputValue.temporaryFile.name.substr(
                                                                    0,
                                                                    inputValue.temporaryFile.name.lastIndexOf(
                                                                        inputValue.temporaryFile.extension
                                                                    )
                                                                )
                                                            );
                                                        }
                                                    },
                                                    inputProps: {
                                                        render: SOURCE_DOCUMENT_UPLOAD_VIEW,
                                                    },
                                                    validators: {
                                                        required: requiredSourceDocument,
                                                    },
                                                    errors: {
                                                        required: "File must be uploaded.",
                                                    },
                                                    errorComponent: ValidationMessage,
                                                },
                                                fileConfiguration: {
                                                    type: RegistrationComponent.RegistrationGridElement,
                                                    label: " ",
                                                    render: SOURCE_DOCUMENT_FILE_CONFIGURATION,
                                                },
                                            },
                                            initialState: {
                                                ...initialSourceDocumentState,
                                                subRecordId: collectionInstanceData.id,
                                            },
                                        },
                                    },
                                    keyReplace: "sourceDocuments",
                                    onRemove: (model: string) => {
                                        changeForm(`${model}active`, false);
                                        return true; // this was handled here
                                    },
                                    filterRow: (data: Dtos.SourceDocument) => {
                                        if (data.active != true || data.subRecordId != collectionInstanceData.id) {
                                            return false;
                                        }
                                        return true;
                                    },
                                };
                            }
                        });
                });
        }

        return (
            <div className="my-container customscrollbar">
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {collectionInstance && collectionInstance.version && (
                        <div className="row border-bottom">
                            <div className="col-12">
                                {columnFormat == "SMALL-LARGE-DOCUMENTS-RIGHT" && (
                                    <div className="row ">
                                        <div className="col-5">
                                            {renderFormControls(
                                                form,
                                                "collectionInstance",
                                                formVersionComponents,
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                        <div className="col-7"></div>
                                    </div>
                                )}
                                {columnFormat == "EVEN" && (
                                    <div className="row">
                                        <div className="col">
                                            <div
                                                className="container"
                                                style={{
                                                    paddingLeft: 0,
                                                    paddingRight: 0,
                                                }}
                                            >
                                                <div className="row">
                                                    {form.column1.length > 0 && (
                                                        <div className="col">
                                                            {renderFormControls(
                                                                form,
                                                                "collectionInstance",
                                                                formVersionComponents,
                                                                lookups,
                                                                validationFailures,
                                                                formProperties,
                                                                changeForm
                                                            )}
                                                        </div>
                                                    )}
                                                    {form.column2.length > 0 && (
                                                        <div className="col"></div>
                                                    )}
                                                    {form.column3.length > 0 && (
                                                        <div className="col"></div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="x-container-fluid">
                        {form && form.sections.map((section, sectionIndex) => (
                            <div
                                key={`map-section-${section.collectionSection.id}`}
                            >
                                {form.hasSections && (
                                    <div
                                        key={`section-${section.collectionSection.id}`}
                                    >
                                        <div
                                            className={
                                                section.isNa
                                                    ? tableHeaderNaClass
                                                    : section.isMissingRequiredData
                                                    ? tableHeaderDangerClass
                                                    : tableHeaderClass
                                            }
                                            style={tableStyleClass}
                                            onClick={() => {
                                                expandSection(section.collectionSection.id);
                                            }}
                                        >
                                            <div
                                                className="d-flex"
                                                style={{ width: "100%" }}
                                            >
                                                <div>
                                                    {!section.isNa && (
                                                        <>
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    !section.expanded
                                                                        ? FontAwesomeIcons.Solid.ANGLE_DOWN
                                                                        : FontAwesomeIcons.Solid.ANGLE_UP
                                                                }
                                                                fixedWidth
                                                                transform="grow-16"
                                                                className="mr-3"
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                                <div className="flex-grow-1">
                                                    <span>
                                                        { section.collectionSection.name }
                                                    </span>
                                                </div>
                                                <div
                                                    className="px-2"
                                                    style={{
                                                        minWidth: "150px",
                                                        margin: "-7px 0",
                                                        fontWeight: "normal",
                                                    }}
                                                >
                                                    <SelectFormInput
                                                        name={`${section.collectionSectionId}section_assigmentType`}
                                                        id={`${section.collectionSectionId}section_assigmentType`}
                                                        value={section.assignmentTypeId}
                                                        loading={false}
                                                        multi={false}
                                                        disabled={section.locked}
                                                        placeholder={"Assign..."}
                                                        options={
                                                            generateOptionsFromLookup(
                                                                findLookup("TrialForm", "AssignmentTypeId", lookups),
                                                                section.assignmentTypeId
                                                            )
                                                        }
                                                        clearable={true}
                                                        backspaceRemoves={true}
                                                        onChange={(v,o) => {
                                                            changeForm(`${reduxFormName}.sections[${sectionIndex}].assignmentTypeId`, v, o);
                                                            return null;
                                                        }}
                                                    />
                                                </div>
                                                <div
                                                    className="px-2"
                                                    style={{
                                                        minWidth: "180px",
                                                        margin: "-7px 0",
                                                        fontWeight: "normal",
                                                    }}
                                                >
                                                    {section.assignmentTypeId == 1 && (
                                                        <SelectFormInput
                                                            name={`${section.collectionSectionId}section_assignedRoleId`}
                                                            id={`${section.collectionSectionId}section_assignedRoleId`}
                                                            value={section.assignedRoleId}
                                                            loading={false}
                                                            multi={false}
                                                            disabled={section.locked}
                                                            placeholder={"To..."}
                                                            options={
                                                                generateOptionsFromLookup(
                                                                    findLookup("TrialForm", "AssignedRoleId", lookups),
                                                                    section.assignedRoleId
                                                                )
                                                            }
                                                            clearable={true}
                                                            backspaceRemoves={true}
                                                            onChange={(v, o) => {
                                                                changeForm(`${reduxFormName}.sections[${sectionIndex}].assignedRoleId`, v,o);
                                                                changeForm(`${reduxFormName}.sections[${sectionIndex}].assignedTo`, null);
                                                                changeForm(`${reduxFormName}.sections[${sectionIndex}].assignedRole`, null);
                                                                return null;
                                                            }}
                                                        />
                                                    )}
                                                    {section.assignmentTypeId == 2 && (
                                                        <SelectFormInput
                                                            name={`${section.collectionSectionId}section_assignedToId`}
                                                            id={`${section.collectionSectionId}section_assignedToId`}
                                                            value={section.assignedToId}
                                                            loading={false}
                                                            multi={false}
                                                            placeholder={"To..."}
                                                            options={
                                                                generateOptionsFromLookup(
                                                                    findLookup("TrialForm", "AssignedToId", lookups),
                                                                    section.assignedToId
                                                                )
                                                            }
                                                            clearable={true}
                                                            backspaceRemoves={true}
                                                            onChange={(v, o) => {
                                                                changeForm(`${reduxFormName}.sections[${sectionIndex}].assignedToId`, v, o);
                                                                changeForm(`${reduxFormName}.sections[${sectionIndex}].assignedTo`, null);
                                                                changeForm(`${reduxFormName}.sections[${sectionIndex}].assignedRole`, null);
                                                                return null;
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                <div
                                                    style={{
                                                        minWidth: "220px",
                                                        textAlign: "right",
                                                    }}
                                                >
                                                    {!section.isNa && (
                                                        <span>
                                                            { section.summary }
                                                        </span>
                                                    )}
                                                </div>
                                                <div
                                                    style={{
                                                        minWidth: "70px",
                                                    }}
                                                >
                                                    {section.collectionSection.isNaAllowed && (
                                                        <div className="pl-4">
                                                            <CheckboxFormInput
                                                                name={`${section.collectionSectionId}section_isNa`}
                                                                id={`${section.collectionSectionId}section_isNa`}
                                                                value={section.isNa }
                                                                onChange={(v, o) => {
                                                                    changeForm(`${reduxFormName}.sections[${sectionIndex}].isNa`, v, o);
                                                                    return null;
                                                                }}
                                                                additionalContent={""}
                                                                disabled={section.locked}
                                                                hideLabel={false}
                                                                label={"N/A"}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {(!form.hasSections || (section.collectionSection.id == sectionId && !section.isNa)) && (
                                    <div className="row">
                                        <div className="col-12 mb-3 mt-3">
                                            {columnFormat == "SMALL-LARGE-DOCUMENTS-RIGHT" && (
                                                <div className="row">
                                                    <div className="col-5">
                                                        {form.column1.map((itemComponentWithData, itemComponentWithDataIndex) => {
                                                            if (itemComponentWithData.collectionSectionId != sectionId) {
                                                                return null;
                                                            }
                                                            return renderItemComponentWithData(
                                                                    itemComponentWithData,
                                                                    `${reduxFormName}.column1[${itemComponentWithDataIndex}].collectionInstanceData`,
                                                                    section.formLocked
                                                                );
                                                        })}
                                                    </div>
                                                    <div className="col-7">
                                                        {form.column2.map((itemComponentWithData, itemComponentWithDataIndex) => {
                                                            if (itemComponentWithData.collectionSectionId != sectionId) {
                                                                return null;
                                                            }
                                                            return renderItemComponentWithData(
                                                                itemComponentWithData,
                                                                `${reduxFormName}.column2[${itemComponentWithDataIndex}].collectionInstanceData`,
                                                                section.formLocked
                                                            );
                                                        })}
                                                        {form.column3.map((itemComponentWithData, itemComponentWithDataIndex) => {
                                                            if (itemComponentWithData.collectionSectionId != sectionId) {
                                                                return null;
                                                            }
                                                            return renderItemComponentWithData(
                                                                itemComponentWithData,
                                                                `${reduxFormName}.column3[${itemComponentWithDataIndex}].collectionInstanceData`,
                                                                section.formLocked
                                                            );
                                                        })}
                                                        {renderFormControls(
                                                            form,
                                                            "collectionInstance",
                                                            formComponents,
                                                            lookups,
                                                            validationFailures,
                                                            formProperties,
                                                            changeForm
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            {columnFormat == "EVEN" && (
                                                <div className="row">
                                                    <div className="col">
                                                        <div
                                                            className="container"
                                                            style={{
                                                                paddingLeft: 0,
                                                                paddingRight: 0,
                                                            }}
                                                        >
                                                            <div className="row">
                                                                {form.column1.length > 0 && (
                                                                    <div className="col">
                                                                        {form.column1.map((itemComponentWithData, itemComponentWithDataIndex) => {
                                                                            if (itemComponentWithData.collectionSectionId != sectionId) {
                                                                                return null;
                                                                            }
                                                                            return renderItemComponentWithData(
                                                                                itemComponentWithData,
                                                                                `${reduxFormName}.column1[${itemComponentWithDataIndex}].collectionInstanceData`,
                                                                                section.formLocked
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                                {form.column2.length > 0 && (
                                                                    <div className="col">
                                                                        {form.column2.map((itemComponentWithData, itemComponentWithDataIndex) => {
                                                                                if (itemComponentWithData.collectionSectionId != sectionId) {
                                                                                    return null;
                                                                                }
                                                                                return renderItemComponentWithData(
                                                                                    itemComponentWithData,
                                                                                    `${reduxFormName}.column2[${itemComponentWithDataIndex}].collectionInstanceData`,
                                                                                    section.formLocked
                                                                                );
                                                                        })}
                                                                    </div>
                                                                )}
                                                                {form.column3.length > 0 && (
                                                                    <div className="col">
                                                                        {form.column3.map((itemComponentWithData, itemComponentWithDataIndex) => {
                                                                                if (itemComponentWithData.collectionSectionId != sectionId) {
                                                                                    return null;
                                                                                }
                                                                                return renderItemComponentWithData(
                                                                                    itemComponentWithData,
                                                                                    `${reduxFormName}.column3[${itemComponentWithDataIndex}].collectionInstanceData`,
                                                                                    section.formLocked
                                                                                );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="row">
                                                                <div className="col">
                                                                    {renderFormControls(
                                                                        form,
                                                                        "collectionInstance",
                                                                        formComponents,
                                                                        lookups,
                                                                        validationFailures,
                                                                        formProperties,
                                                                        changeForm
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        <div 
                            className="row" 
                            style={{ 
                                marginTop: "10px"
                            }}
                        >
                            <div 
                                className="col" 
                                style={{ 
                                    textAlign: "right" 
                                }}
                            >
                                <button
                                    disabled={formDisabled || !isValid}
                                    onClick={() => {
                                        changeForm(`${reduxFormName}.saveAndReturn`, false);
                                    }}
                                    type="submit"
                                    className="btn btn-primary mr-2"
                                >
                                    Submit
                                </button>
                                <button
                                    disabled={formDisabled || !isValid}
                                    onClick={() => {
                                        changeForm(`${reduxFormName}.saveAndReturn`, true);
                                    }}
                                    type="submit"
                                    className="btn btn-primary mr-2"
                                >
                                    Submit And Return
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </Form>
            </div>
        );
    }, [
        readonly,
        savingCollectionInstance,
        formState,
        collectionInstance,
        form,
        documents,
        getFormPropertyLabel,
        formProperties,
        generateOptionsFromLookup,
        findLookup,
        lookups,
        changeForm,
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        validationFailures,
        tableHeaderNaClass,
        tableHeaderDangerClass,
        tableHeaderClass,
        tableStyleClass,
        expandSection,
        renderItemComponentWithData,
        isValid,
        onCancel
    ]);

    return (
        <SubPageLayout 
            loading={loadingCollectionInstance || loadingItemComponents}
        >
            { renderContent }
        </SubPageLayout>
    );
};

const mapStateToProps = (state: ITmdState, ownProps): Partial<CollectionInstanceComponentProps> => {
    let collectionInstance: Dtos.CollectionInstance | undefined = !(state.collectionInstance.formData instanceof Array)
        ? state.collectionInstance.formData
        : undefined;

    let collection: Dtos.Collection | undefined = collectionInstance && collectionInstance.collection
        ? collectionInstance.collection
        : undefined;

    const itemComponents: Dtos.ItemComponent[] | undefined = state.itemComponent.formData instanceof Array
        ? state.itemComponent.formData ?? []
        : undefined;

    return {
        readonly: ownProps.readonly,
        collectionInstanceId: ownProps.collectionInstanceId
            ? ownProps.collectionInstanceId
            : undefined!,
        collectionInstance: collectionInstance!,
        collection: collection!,
        loadingCollectionInstance: state.collectionInstance.loadState && state.collectionInstance.loadState.status == RequestState.Pending,
        loadCollectionInstanceSuccess: state.collectionInstance.loadState && state.collectionInstance.loadState.status == RequestState.Success,
        loadCollectionInstanceFailure: state.collectionInstance.loadState && state.collectionInstance.loadState.status == RequestState.Failure,
        loadCollectionInstanceErrorCode: state.collectionInstance.loadState.errorCode,
        savingCollectionInstance: state.collectionInstance.saveState && state.collectionInstance.saveState.status == RequestState.Pending,
        saveCollectionInstanceSuccess: state.collectionInstance.saveState && state.collectionInstance.saveState.status == RequestState.Success,
        saveCollectionInstanceFailure: state.collectionInstance.saveState && state.collectionInstance.saveState.status == RequestState.Failure,
        lookups: state.collectionInstance.lookups,
        form: state.reduxForms.collectionInstance,
        formState: state.collectionInstance.formState,
        formProperties: state.collectionInstance.formProperties,
        validationFailures: state.collectionInstance.validationFailures,
        itemComponents: itemComponents,
        loadingItemComponents: state.itemComponent.loadState && state.itemComponent.loadState.status == RequestState.Pending,
        loadItemComponentsSuccess: state.itemComponent.loadState && state.itemComponent.loadState.status == RequestState.Success,
        loadItemComponentsFailure: state.itemComponent.loadState && state.itemComponent.loadState.status == RequestState.Failure,
        loadItemComponentsErrorCode: state.itemComponent.loadState.errorCode,
        documents: filterCollectionItemComponentsByDocuments(collection),
    };
};

const mapDispatchToProps = (dispatch): Partial<CollectionInstanceComponentProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadCollectionInstance: bindActionCreators( CollectionInstanceActions.LoadCollectionInstanceById, dispatch),
        clearCollectionInstance: bindActionCreators(CollectionInstanceActions.Clear, dispatch),
        saveCollectionInstance: bindActionCreators(CollectionInstanceActions.SaveCollectionInstance, dispatch),
        expandSection: bindActionCreators(CollectionInstanceActions.ExpandSection, dispatch),
        loadItemComponents: bindActionCreators(ItemComponentActions.LoadItemComponent, dispatch),
        clearItemComponents: bindActionCreators(ItemComponentActions.Clear, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(CollectionInstanceComponent);